import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Database } from '../../lib/database.types';
import { supabase } from '../../lib/supabase';
import type {
  CustomerOption,
  LeadOption,
  LineItemInput,
  LineItemRow,
  QuoteInput,
  QuoteRow,
  QuoteStatus,
} from './quote.schema';
import { computeTotal } from './quote.schema';

type QuoteInsert = Database['public']['Tables']['quotes']['Insert'];
type LineItemInsert = Database['public']['Tables']['quote_line_items']['Insert'];

export const quotesQueryKey = (orgId: string | null) => ['quotes', orgId] as const;
export const quoteQueryKey = (quoteId: string | null) => ['quote', quoteId] as const;
export const quoteItemsQueryKey = (quoteId: string | null) => ['quote-items', quoteId] as const;
export const customersForQuoteKey = (orgId: string | null) => ['quote-customers', orgId] as const;
export const leadsForQuoteKey = (orgId: string | null) => ['quote-leads', orgId] as const;

async function fetchQuotes(orgId: string): Promise<QuoteRow[]> {
  const { data, error } = await supabase
    .from('quotes')
    .select(
      'id, quote_number, status, customer_id, primary_contact_id, source_lead_id, value_amount, value_currency, valid_until, sent_at, viewed_at, accepted_at, rejected_at, notes, created_at, updated_at',
    )
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

async function fetchQuote(orgId: string, quoteId: string): Promise<QuoteRow | null> {
  const { data, error } = await supabase
    .from('quotes')
    .select(
      'id, quote_number, status, customer_id, primary_contact_id, source_lead_id, value_amount, value_currency, valid_until, sent_at, viewed_at, accepted_at, rejected_at, notes, created_at, updated_at',
    )
    .eq('organization_id', orgId)
    .eq('id', quoteId)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) throw error;
  return data;
}

async function fetchQuoteItems(orgId: string, quoteId: string): Promise<LineItemRow[]> {
  const { data, error } = await supabase
    .from('quote_line_items')
    .select('id, quote_id, service_label, description, quantity, unit_price, subtotal, sort_order, created_at')
    .eq('organization_id', orgId)
    .eq('quote_id', quoteId)
    .is('deleted_at', null)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return data;
}

async function fetchCustomers(orgId: string): Promise<CustomerOption[]> {
  const { data, error } = await supabase
    .from('companies')
    .select('id, name')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .order('name', { ascending: true });
  if (error) throw error;
  return data;
}

async function fetchLeads(orgId: string): Promise<LeadOption[]> {
  const { data, error } = await supabase
    .from('leads')
    .select('id, full_name, company_name')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .in('status', ['new', 'contacted', 'qualified', 'quoted'])
    .order('received_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return data;
}

export function useQuotes(orgId: string | null) {
  return useQuery({
    queryKey: quotesQueryKey(orgId),
    queryFn: () => (orgId ? fetchQuotes(orgId) : Promise.resolve<QuoteRow[]>([])),
    enabled: Boolean(orgId),
    staleTime: 30 * 1000,
  });
}

export function useQuote(orgId: string | null, quoteId: string | null) {
  return useQuery({
    queryKey: quoteQueryKey(quoteId),
    queryFn: () =>
      orgId && quoteId ? fetchQuote(orgId, quoteId) : Promise.resolve<QuoteRow | null>(null),
    enabled: Boolean(orgId && quoteId),
    staleTime: 15 * 1000,
  });
}

export function useQuoteItems(orgId: string | null, quoteId: string | null) {
  return useQuery({
    queryKey: quoteItemsQueryKey(quoteId),
    queryFn: () =>
      orgId && quoteId ? fetchQuoteItems(orgId, quoteId) : Promise.resolve<LineItemRow[]>([]),
    enabled: Boolean(orgId && quoteId),
    staleTime: 15 * 1000,
  });
}

export function useCustomersForQuote(orgId: string | null) {
  return useQuery({
    queryKey: customersForQuoteKey(orgId),
    queryFn: () => (orgId ? fetchCustomers(orgId) : Promise.resolve<CustomerOption[]>([])),
    enabled: Boolean(orgId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useLeadsForQuote(orgId: string | null) {
  return useQuery({
    queryKey: leadsForQuoteKey(orgId),
    queryFn: () => (orgId ? fetchLeads(orgId) : Promise.resolve<LeadOption[]>([])),
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
  });
}

function emptyOrNull(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function useCreateQuote(orgId: string | null, userId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: QuoteInput): Promise<string> => {
      if (!orgId || !userId) throw new Error('Missing quote context');
      const total = computeTotal(values.items);

      const insertPayload: QuoteInsert = {
        organization_id: orgId,
        quote_number: '', // trigger assigns Q-YYYY-NNNN
        customer_id: values.customer_id ?? null,
        primary_contact_id: values.primary_contact_id ?? null,
        source_lead_id: values.source_lead_id ?? null,
        status: values.status ?? 'draft',
        value_amount: total,
        value_currency: 'ILS',
        valid_until: emptyOrNull(values.valid_until ?? null),
        notes: emptyOrNull(values.notes ?? null),
        created_by: userId,
      };

      const { data: quote, error } = await supabase
        .from('quotes')
        .insert(insertPayload)
        .select('id')
        .single();
      if (error) throw error;

      const itemPayloads: LineItemInsert[] = values.items.map((item, index) => ({
        organization_id: orgId,
        quote_id: quote.id,
        service_label: item.service_label.trim(),
        description: emptyOrNull(item.description ?? null),
        quantity: item.quantity,
        unit_price: item.unit_price,
        sort_order: index,
      }));

      const { error: itemsErr } = await supabase.from('quote_line_items').insert(itemPayloads);
      if (itemsErr) throw itemsErr;

      // If created from a lead: link the quote, then advance the lead to 'quoted'
      // through the lifecycle engine (entity-action). This keeps a single authority
      // for status changes — it writes one timeline activity and is a no-op if the
      // lead is already at/past 'quoted'. The wizard is the single source of quote
      // creation; the status change never creates a quote.
      if (values.source_lead_id) {
        await supabase
          .from('leads')
          .update({ linked_quote_id: quote.id })
          .eq('id', values.source_lead_id)
          .eq('organization_id', orgId);
        const advance = await supabase.functions.invoke<{ success?: boolean }>('entity-action', {
          body: {
            action: 'change_status',
            entity_type: 'lead',
            entity_id: values.source_lead_id,
            to_status: 'quoted',
          },
        });
        // Best-effort: the quote is already created + linked. If the lead can't
        // advance (e.g. it was already converted), leave its status untouched.
        if (advance.error) {
          const detail = advance.error instanceof Error ? advance.error.message : 'unknown error';
          console.warn('[useCreateQuote] lead advance to quoted skipped:', detail);
        }
      }

      return quote.id;
    },
    onSuccess: async (_id, vars) => {
      await queryClient.invalidateQueries({ queryKey: quotesQueryKey(orgId) });
      await queryClient.invalidateQueries({ queryKey: ['leads', orgId] });
      if (vars.source_lead_id) {
        await queryClient.invalidateQueries({ queryKey: ['lead', vars.source_lead_id] });
      }
    },
  });
}

export function useUpdateQuoteStatus(orgId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ quoteId, status }: { quoteId: string; status: QuoteStatus }) => {
      const updates: Database['public']['Tables']['quotes']['Update'] = { status };
      const now = new Date().toISOString();
      if (status === 'sent') updates.sent_at = now;
      if (status === 'accepted') updates.accepted_at = now;
      if (status === 'rejected') updates.rejected_at = now;
      const { error } = await supabase.from('quotes').update(updates).eq('id', quoteId);
      if (error) throw error;
    },
    onSuccess: async (_v, { quoteId }) => {
      await queryClient.invalidateQueries({ queryKey: quotesQueryKey(orgId) });
      await queryClient.invalidateQueries({ queryKey: quoteQueryKey(quoteId) });
    },
  });
}

export function useReplaceQuoteItems(orgId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ quoteId, items }: { quoteId: string; items: LineItemInput[] }) => {
      if (!orgId) throw new Error('Missing org context');
      // Soft-delete existing items then insert new ones.
      const { error: delErr } = await supabase
        .from('quote_line_items')
        .update({ deleted_at: new Date().toISOString() })
        .eq('organization_id', orgId)
        .eq('quote_id', quoteId)
        .is('deleted_at', null);
      if (delErr) throw delErr;

      const payloads: LineItemInsert[] = items.map((item, index) => ({
        organization_id: orgId,
        quote_id: quoteId,
        service_label: item.service_label.trim(),
        description: emptyOrNull(item.description ?? null),
        quantity: item.quantity,
        unit_price: item.unit_price,
        sort_order: index,
      }));
      const { error: insErr } = await supabase.from('quote_line_items').insert(payloads);
      if (insErr) throw insErr;

      const total = computeTotal(items);
      const { error: totalErr } = await supabase
        .from('quotes')
        .update({ value_amount: total })
        .eq('id', quoteId);
      if (totalErr) throw totalErr;
    },
    onSuccess: async (_v, { quoteId }) => {
      await queryClient.invalidateQueries({ queryKey: quotesQueryKey(orgId) });
      await queryClient.invalidateQueries({ queryKey: quoteItemsQueryKey(quoteId) });
      await queryClient.invalidateQueries({ queryKey: quoteQueryKey(quoteId) });
    },
  });
}

export function useSoftDeleteQuote(orgId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (quoteId: string) => {
      const { error } = await supabase
        .from('quotes')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', quoteId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: quotesQueryKey(orgId) });
    },
  });
}

interface ConvertResult {
  customer_id: string;
  project_id: string;
}

/**
 * Convert an accepted quote into a real Customer + Project.
 *
 * Pipeline:
 *  1. Load quote (must be status='accepted'); pull source_lead_id and customer_id.
 *  2. Resolve customer:
 *       - quote.customer_id if set
 *       - else lead.linked_customer_id if lead exists
 *       - else create new company from lead.company_name OR lead.full_name
 *  3. Ensure quote.customer_id is populated.
 *  4. Create project: status='active', quote_id, customer_id, primary_contact_id from
 *     quote, source_lead_id from quote, value_amount from quote total, currency.
 *  5. If lead exists: status='converted', linked_customer_id, linked_quote_id=quote.id.
 *
 * Idempotency: caller responsible for checking quote isn't already converted (e.g. by
 * inspecting whether projects.quote_id row exists). V1 does not de-dupe on its own.
 */
export function useAcceptQuoteToProject(orgId: string | null, userId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ quoteId, projectName }: { quoteId: string; projectName: string }): Promise<ConvertResult> => {
      if (!orgId || !userId) throw new Error('Missing convert context');

      // 1. Load quote
      const { data: quote, error: qErr } = await supabase
        .from('quotes')
        .select('id, status, customer_id, primary_contact_id, source_lead_id, value_amount, value_currency')
        .eq('id', quoteId)
        .eq('organization_id', orgId)
        .single();
      if (qErr) throw qErr;
      if (quote.status !== 'accepted') {
        throw new Error('Quote must be accepted before conversion');
      }

      // 2. Resolve customer
      let customerId = quote.customer_id;
      let leadFullName: string | null = null;
      let leadCompanyName: string | null = null;
      let leadCustomerId: string | null = null;

      if (quote.source_lead_id) {
        const { data: lead } = await supabase
          .from('leads')
          .select('full_name, company_name, linked_customer_id')
          .eq('id', quote.source_lead_id)
          .eq('organization_id', orgId)
          .maybeSingle();
        if (lead) {
          leadFullName = lead.full_name;
          leadCompanyName = lead.company_name;
          leadCustomerId = lead.linked_customer_id;
        }
      }

      if (!customerId && leadCustomerId) {
        customerId = leadCustomerId;
      }

      if (!customerId) {
        const trimmedCompany = leadCompanyName?.trim();
        const seedName = (trimmedCompany && trimmedCompany.length > 0 ? trimmedCompany : null)
          ?? leadFullName
          ?? 'לקוח חדש';
        const { data: created, error: cErr } = await supabase
          .from('companies')
          .insert({
            organization_id: orgId,
            name: seedName,
            color: 'yellow',
          })
          .select('id')
          .single();
        if (cErr) throw cErr;
        customerId = created.id;
      }

      // 3. Update quote.customer_id if missing
      if (!quote.customer_id) {
        await supabase
          .from('quotes')
          .update({ customer_id: customerId })
          .eq('id', quoteId)
          .eq('organization_id', orgId);
      }

      // 4. Create project
      const { data: project, error: pErr } = await supabase
        .from('projects')
        .insert({
          organization_id: orgId,
          name: projectName.trim(),
          status: 'active',
          quote_id: quoteId,
          company_id: customerId,
          primary_contact_id: quote.primary_contact_id,
          source_lead_id: quote.source_lead_id,
          value_amount: quote.value_amount,
          value_currency: quote.value_currency,
          owned_by: userId,
          created_by: userId,
        })
        .select('id')
        .single();
      if (pErr) throw pErr;

      // 5. Update lead
      if (quote.source_lead_id) {
        await supabase
          .from('leads')
          .update({
            status: 'converted',
            linked_customer_id: customerId,
            linked_quote_id: quoteId,
          })
          .eq('id', quote.source_lead_id)
          .eq('organization_id', orgId);
      }

      return { customer_id: customerId, project_id: project.id };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: quotesQueryKey(orgId) });
      await queryClient.invalidateQueries({ queryKey: ['leads', orgId] });
      await queryClient.invalidateQueries({ queryKey: ['companies', orgId] });
      await queryClient.invalidateQueries({ queryKey: ['projects', orgId] });
    },
  });
}
